/**
 * Helper genérico para detectar cambios en objetos y construir patches eficientes.
 * Diseñado para ser reutilizable en cualquier entidad.
 */

/**
 * Opciones para comparación de objetos
 */
export interface DeepEqualOptions {
  excludeKeys?: string[]
}

/**
 * Resultado del análisis de cambios en un array
 */
export interface ArrayChangeResult<T> {
  toCreate: T[]
  toUpdate: T[]
  toDelete: any[] // IDs o items completos para eliminar
  hasChanges: boolean
}

/**
 * Resultado del análisis de cambios en propiedades primitivas
 */
export interface PrimitiveChangesResult {
  changed: Record<string, any>
  hasChanges: boolean
}

/**
 * Compara dos valores de forma profunda (excluyendo propiedades específicas)
 */
export function deepEqual(
  obj1: any,
  obj2: any,
  options: DeepEqualOptions = { excludeKeys: ['tempId'] }
): boolean {
  const excludeKeys = options.excludeKeys || []

  // Comparación directa
  if (obj1 === obj2) return true

  // Null/undefined
  if (obj1 == null || obj2 == null) return false

  // Tipos diferentes
  if (typeof obj1 !== typeof obj2) return false

  // Tipos primitivos
  if (typeof obj1 !== 'object') return obj1 === obj2

  // Arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false
    return obj1.every((item, index) => deepEqual(item, obj2[index], options))
  }

  // Objetos
  const keys1 = Object.keys(obj1).filter(k => !excludeKeys.includes(k))
  const keys2 = Object.keys(obj2).filter(k => !excludeKeys.includes(k))

  if (keys1.length !== keys2.length) return false

  return keys1.every(key => keys2.includes(key) && deepEqual(obj1[key], obj2[key], options))
}

/**
 * Crea una copia profunda de un objeto para tracking
 */
export function cloneForTracking<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}

/**
 * Detecta cambios en propiedades primitivas de un objeto
 * @param original - Objeto original
 * @param current - Objeto actual
 * @param primitiveKeys - Array de nombres de propiedades a comparar
 * @returns Objeto con solo las propiedades que cambiaron
 */
export function detectPrimitiveChanges(
  original: Record<string, any>,
  current: Record<string, any>,
  primitiveKeys: string[]
): PrimitiveChangesResult {
  const changed: Record<string, any> = {}
  let hasChanges = false

  primitiveKeys.forEach(key => {
    if (original[key] !== current[key]) {
      changed[key] = current[key]
      hasChanges = true
    }
  })

  return { changed, hasChanges }
}

/**
 * Detecta cambios en un array de objetos (CRUD operations)
 * @param original - Array original
 * @param current - Array actual
 * @param idKey - Nombre de la propiedad que actúa como ID (default: 'id')
 * @param tempIdKey - Nombre de la propiedad temporal para nuevos items (default: 'tempId')
 * @param options - Opciones de comparación
 * @returns Objetos a crear, actualizar y eliminar
 */
export function detectArrayChanges<T extends Record<string, any>>(
  original: T[],
  current: T[],
  idKey: keyof T = 'id' as keyof T,
  tempIdKey: string = 'tempId',
  options: DeepEqualOptions = { excludeKeys: ['tempId'] }
): ArrayChangeResult<T> {
  const toCreate: T[] = []
  const toUpdate: T[] = []
  const toDelete: any[] = []

  // Detectar items a CREAR (solo si NO tienen id)
  // El tempId es solo para la UI y no afecta si es create o update
  current.forEach(item => {
    if (!item[idKey]) {
      toCreate.push(item)
    }
  })

  // Detectar items a ACTUALIZAR (tienen id y cambiaron)
  // El tempId no importa aquí - si tiene id, puede ser update
  current.forEach(currentItem => {
    if (currentItem[idKey]) {
      const originalItem = original.find(o => o[idKey] === currentItem[idKey])
      if (originalItem && !deepEqual(originalItem, currentItem, options)) {
        toUpdate.push(currentItem)
      }
    }
  })

  // Detectar items a ELIMINAR (estaban en original pero no en current)
  original.forEach(originalItem => {
    if (originalItem[idKey]) {
      const stillExists = current.find(c => c[idKey] === originalItem[idKey])
      if (!stillExists) {
        toDelete.push(originalItem[idKey])
      }
    }
  })

  const hasChanges = toCreate.length > 0 || toUpdate.length > 0 || toDelete.length > 0

  return { toCreate, toUpdate, toDelete, hasChanges }
}

/**
 * Detecta si un objeto ha cambiado (comparación completa)
 * Útil para validar antes de guardar
 */
export function hasObjectChanged(
  original: any,
  current: any,
  options: DeepEqualOptions = { excludeKeys: ['tempId'] }
): boolean {
  return !deepEqual(original, current, options)
}
